// src/components/dashboard/ProfileModal.jsx
import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    FormControl,
    FormLabel,
    Input,
    VStack,
    useToast,
    Text,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    InputGroup,
    InputRightElement,
    HStack,
} from '@chakra-ui/react';
import { supabase } from '../../config/supabaseClient';
import InputMask from 'react-input-mask';
import { cpf, cnpj } from 'cpf-cnpj-validator';

const ProfileModal = ({ isOpen, onClose, userId, onProfileUpdate }) => {
    const [loading, setLoading] = useState(false);
    const [loadingCep, setLoadingCep] = useState(false);
    const [profile, setProfile] = useState({
        name: '',
        email: '',
        cpf_cnpj: '',
        phone: '',
        postal_code: '',
        address: '',
        address_number: '',
        city: '',
        state: '',
        neighborhood: '',
        complement: ''
    });
    const toast = useToast();

    useEffect(() => {
        if (isOpen && userId) {
            fetchProfile();
        }
    }, [isOpen, userId]);

    const fetchProfile = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

            if (error) throw error;

            setProfile({
                name: data.name || '',
                email: data.email || '',
                cpf_cnpj: data.cpf_cnpj || '',
                phone: data.phone || '',
                postal_code: data.postal_code || '',
                address: data.address || '',
                address_number: data.address_number || '',
                city: data.city || '',
                state: data.state || '',
                neighborhood: data.neighborhood || '',
                complement: data.complement || ''
            });
        } catch (error) {
            console.error('Erro ao carregar perfil:', error);
            toast({
                title: 'Erro ao carregar perfil',
                description: error.message,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSearchCep = async () => {
        const cep = profile.postal_code.replace(/\D/g, '');

        if (cep.length !== 8) {
            toast({
                title: 'CEP inválido',
                description: 'Por favor, digite um CEP válido com 8 dígitos.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setLoadingCep(true);

        try {
            const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
            const data = await response.json();

            if (data.erro) {
                toast({
                    title: 'CEP não encontrado',
                    description: 'O CEP informado não foi encontrado.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            setProfile(prev => ({
                ...prev,
                address: data.logradouro,
                neighborhood: data.bairro,
                city: data.localidade,
                state: data.uf
            }));

            toast({
                title: 'Endereço encontrado',
                description: 'Os dados de endereço foram preenchidos automaticamente.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Erro ao buscar CEP:', error);
            toast({
                title: 'Erro na busca',
                description: 'Ocorreu um erro ao buscar o CEP. Tente novamente.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingCep(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setProfile(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = () => {
        // Validar CPF/CNPJ
        const cpfCnpjClean = profile.cpf_cnpj.replace(/[^0-9]/g, '');
        const isCpfByLength = cpfCnpjClean.length <= 11;

        // Usar a biblioteca para validar CPF ou CNPJ
        const isDocumentValid = isCpfByLength
            ? cpf.isValid(profile.cpf_cnpj)
            : cnpj.isValid(profile.cpf_cnpj);

        if (!isDocumentValid) {
            toast({
                title: isCpfByLength ? 'CPF inválido' : 'CNPJ inválido',
                description: `Por favor, insira um ${isCpfByLength ? 'CPF' : 'CNPJ'} válido.`,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return false;
        }

        // Validar CEP
        if (!profile.postal_code || profile.postal_code.replace(/\D/g, '').length !== 8) {
            toast({
                title: 'CEP inválido',
                description: 'Por favor, insira um CEP válido com 8 dígitos.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return false;
        }

        // Validar número do endereço
        if (!profile.address_number) {
            toast({
                title: 'Número do endereço obrigatório',
                description: 'Por favor, insira o número do seu endereço.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!validateForm()) {
            setLoading(false);
            return;
        }

        try {
            const { error } = await supabase
                .from('profiles')
                .update({
                    name: profile.name,
                    cpf_cnpj: profile.cpf_cnpj,
                    phone: profile.phone,
                    postal_code: profile.postal_code,
                    address: profile.address,
                    address_number: profile.address_number,
                    city: profile.city,
                    state: profile.state,
                    neighborhood: profile.neighborhood,
                    complement: profile.complement
                })
                .eq('id', userId);

            if (error) throw error;

            toast({
                title: 'Perfil atualizado',
                description: 'Seu perfil foi atualizado com sucesso.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            if (onProfileUpdate) {
                onProfileUpdate();
            }

            onClose();
        } catch (error) {
            console.error('Erro ao atualizar perfil:', error);
            toast({
                title: 'Erro ao atualizar perfil',
                description: error.message,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Atualizar Perfil</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {(!profile.cpf_cnpj || !profile.postal_code || !profile.address_number) && (
                        <Alert status="warning" mb={4}>
                            <AlertIcon />
                            <AlertTitle>Atenção!</AlertTitle>
                            <AlertDescription>
                                Para realizar pagamentos, é necessário preencher todos os campos obrigatórios marcados com *.
                            </AlertDescription>
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <VStack spacing={4}>
                            <FormControl id="name" isRequired>
                                <FormLabel>Nome Completo *</FormLabel>
                                <Input
                                    name="name"
                                    value={profile.name}
                                    onChange={handleInputChange}
                                />
                            </FormControl>

                            <FormControl id="email" isDisabled>
                                <FormLabel>Email</FormLabel>
                                <Input
                                    name="email"
                                    value={profile.email}
                                    readOnly
                                />
                                <Text fontSize="sm" color="gray.500">
                                    O email não pode ser alterado.
                                </Text>
                            </FormControl>

                            <FormControl id="cpf_cnpj" isRequired>
                                <FormLabel>CPF/CNPJ *</FormLabel>
                                <Input
                                    as={InputMask}
                                    mask={profile.cpf_cnpj.replace(/[^0-9]/g, '').length > 11 ? "99.999.999/9999-99" : "999.999.999-99"}
                                    name="cpf_cnpj"
                                    value={profile.cpf_cnpj}
                                    onChange={handleInputChange}
                                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                                />
                            </FormControl>

                            <FormControl id="phone" isRequired>
                                <FormLabel>Telefone *</FormLabel>
                                <Input
                                    as={InputMask}
                                    mask="(99) 99999-9999"
                                    name="phone"
                                    value={profile.phone}
                                    onChange={handleInputChange}
                                    placeholder="(00) 00000-0000"
                                />
                            </FormControl>

                            {/* Campos de endereço */}
                            <FormControl id="postal_code" isRequired>
                                <FormLabel>CEP *</FormLabel>
                                <InputGroup>
                                    <Input
                                        as={InputMask}
                                        mask="99999-999"
                                        name="postal_code"
                                        value={profile.postal_code}
                                        onChange={handleInputChange}
                                        placeholder="00000-000"
                                    />
                                    <InputRightElement width="4.5rem">
                                        <Button
                                            h="1.75rem"
                                            size="sm"
                                            onClick={handleSearchCep}
                                            isLoading={loadingCep}
                                        >
                                            Buscar
                                        </Button>
                                    </InputRightElement>
                                </InputGroup>
                            </FormControl>

                            <FormControl id="address">
                                <FormLabel>Logradouro</FormLabel>
                                <Input
                                    name="address"
                                    value={profile.address}
                                    onChange={handleInputChange}
                                    placeholder="Rua, Avenida, etc."
                                />
                            </FormControl>

                            <HStack spacing={4}>
                                <FormControl id="address_number" isRequired>
                                    <FormLabel>Número *</FormLabel>
                                    <Input
                                        name="address_number"
                                        value={profile.address_number}
                                        onChange={handleInputChange}
                                        placeholder="123"
                                    />
                                </FormControl>

                                <FormControl id="complement">
                                    <FormLabel>Complemento</FormLabel>
                                    <Input
                                        name="complement"
                                        value={profile.complement}
                                        onChange={handleInputChange}
                                        placeholder="Apto, Bloco, etc."
                                    />
                                </FormControl>
                            </HStack>

                            <FormControl id="neighborhood">
                                <FormLabel>Bairro</FormLabel>
                                <Input
                                    name="neighborhood"
                                    value={profile.neighborhood}
                                    onChange={handleInputChange}
                                    placeholder="Bairro"
                                />
                            </FormControl>

                            <HStack spacing={4}>
                                <FormControl id="city">
                                    <FormLabel>Cidade</FormLabel>
                                    <Input
                                        name="city"
                                        value={profile.city}
                                        onChange={handleInputChange}
                                        placeholder="Cidade"
                                    />
                                </FormControl>

                                <FormControl id="state">
                                    <FormLabel>Estado</FormLabel>
                                    <Input
                                        name="state"
                                        value={profile.state}
                                        onChange={handleInputChange}
                                        placeholder="UF"
                                        maxLength={2}
                                    />
                                </FormControl>
                            </HStack>
                        </VStack>
                    </form>
                </ModalBody>

                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button
                        colorScheme="blue"
                        onClick={handleSubmit}
                        isLoading={loading}
                    >
                        Salvar
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ProfileModal;